import { Patient } from "@/components/PatientCard";
import { cn } from "@/lib/utils";
import { usePatientTATTimer } from "@/hooks/usePatientTATTimer";
import { useMDCompletionTime } from "@/hooks/useMDCompletionTime";
import { Badge } from "@/components/ui/badge";
import { Stethoscope } from "lucide-react";
import { getTATColorClass } from "@/lib/tatHelpers";

interface PatientCalendarCardProps {
  patient: Patient;
  onClick: (patient: Patient) => void;
}

const getTATBorderClass = (status?: string) => {
  switch (status) {
    case 'green': return 'border-l-success';
    case 'yellow': return 'border-l-warning';
    case 'red': return 'border-l-destructive';
    case 'exceeded': return 'border-l-destructive border-l-[6px]';
    default: return 'border-l-border';
  }
};

const getTATBackgroundClass = (status?: string) => {
  switch (status) {
    case 'green': return 'bg-success/5';
    case 'yellow': return 'bg-warning/5';
    case 'red': return 'bg-destructive/5';
    case 'exceeded': return 'bg-destructive/10';
    default: return 'bg-card';
  }
};

export const PatientCalendarCard = ({ patient, onClick }: PatientCalendarCardProps) => {
  const tatTimer = usePatientTATTimer(patient.uuid || patient.id);
  const { data: mdCompletionTime } = useMDCompletionTime(patient.uuid || patient.id);
  
  // Check if patient is fully discharged
  const isDischarged = patient.dischargeStatus === "discharged";
  // Check if discharge is initiated (MD activity completed)
  const isInitiated = !!mdCompletionTime;
  const isNotInitiatedActive = !isDischarged && !isInitiated;

  return (
    <div
      onClick={() => onClick(patient)}
      className={cn(
        "rounded-lg border border-l-4 hover:shadow-lg hover:scale-[1.02] transition-all duration-200 cursor-pointer p-1.5 space-y-0.5 overflow-hidden min-w-0",
        // Discharged: keep success styling
        isDischarged
          ? 'border-l-success bg-success/10 dark:bg-success/20 dark:text-foreground'
          // Not initiated (Awaiting MD): neutral / grey styling
          : isNotInitiatedActive
          ? 'border-l-border bg-card'
          // Otherwise use TAT-based colors
          : cn(getTATBorderClass(tatTimer?.status), getTATBackgroundClass(tatTimer?.status))
      )}
    >
      {/* Patient Name + TAT Timer */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate min-w-0">
          {patient.name}
        </div>
        {!isDischarged && tatTimer?.countdown && (
          <div className={cn("text-xs font-medium whitespace-nowrap flex-shrink-0", getTATColorClass(tatTimer.status))}>
            {tatTimer.countdown}
          </div>
        )}
      </div>

      {/* Patient Info Row: ID • Age • Gender */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 overflow-hidden">
        <span className="font-medium truncate min-w-0">{patient.id}</span>
        <span className="flex-shrink-0">•</span>
        <span className="flex-shrink-0">{patient.age}y</span>
        <span className="flex-shrink-0">•</span>
        <span className="flex-shrink-0">{patient.gender}</span>
      </div>

      {/* Doctor */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Stethoscope className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{patient.doctor}</span>
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-1 pt-0.5">
        {isDischarged ? (
          <Badge className="text-xs px-1.5 py-0 bg-primary text-primary-foreground hover:bg-primary">
            Discharged
          </Badge>
        ) : isInitiated ? (
          <Badge variant="success" className="text-xs px-1.5 py-0">
            Initiated
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            Not Initiated
          </Badge>
        )}
      </div>
    </div>
  );
};
